import React from "react";

/*
  Icon
  Purpose: the app's stroke icon set — a single 24px grid, 1.8 stroke, round
  caps and joins. Replaces the OS emoji that used to stand in for icons in the
  project sidebar, the admin sidebar and elsewhere.

  Why not emoji: they rasterise differently on every platform (flat on Windows,
  glossy on macOS), so the same screen looked like a different product per OS;
  they carry their own fixed colours, so they could never tint with the row
  state — an active nav row put a multicoloured emoji on a lilac fill; and they
  blur when scaled.

  Every path is stroked with `currentColor`, so an icon takes the colour of
  whatever holds it. Set the colour on the parent, never on the icon.

  Usage:
    <Icon name="results" />
    <Icon name="heaps" size={22} />
*/

// A path is either a `d` string, or { d, fill: true } for the rare solid dot.
const PATHS = {
  // ── Project sidebar ──────────────────────────────────────────────────────
  overview: ["M3 20h18", "M6 20v-6", "M12 20v-11", "M18 20v-8"],
  management: ["M9 4h6v3H9z", "M15 5.5h3v15H6v-15h3"],
  board: ["M4 5h4v14H4z", "M10 5h4v9h-4z", "M16 5h4v12h-4z"],
  templates: ["M4 5h16v14H4z", "M4 10h16", "M10 10v9"],

  heapLeach: ["M2.5 19h19L16 7H8L2.5 19z"],
  heaps: ["M2.5 19h19L16 7H8L2.5 19z", "M6 14h12"],
  kinetics: [
    "M9.5 3h5",
    "M10.5 3v6l-4.7 8.4A1.5 1.5 0 0 0 7.1 20h9.8a1.5 1.5 0 0 0 1.3-2.6L13.5 9V3",
    "M8 15h8",
  ],
  kineticsFitting: [
    "M3 19h18",
    "M4 16c5 0 6-9 16-11",
    "M8 11.1a1.4 1.4 0 1 0 0 2.8 1.4 1.4 0 0 0 0-2.8z",
    "M13 7.1a1.4 1.4 0 1 0 0 2.8 1.4 1.4 0 0 0 0-2.8z",
  ],
  hydrodynamics: [
    "M12 3.5s5.5 6 5.5 9.8a5.5 5.5 0 0 1-11 0C6.5 9.5 12 3.5 12 3.5z",
    "M9 14.5q1.5-1.6 3 0t3 0",
  ],
  leaching: ["M12 3.5s5.5 6 5.5 9.8a5.5 5.5 0 0 1-11 0C6.5 9.5 12 3.5 12 3.5z"],
  run: ["M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17z", "M10.2 8.4l6 3.6-6 3.6z"],
  results: ["M3 20h18", "M6 15.5l4-4.5 3 3 5.5-6.5", "M15 7.5h3.5V11"],

  siteWater: ["M3 20h18", "M12 4.5s4.5 4.8 4.5 8a4.5 4.5 0 0 1-9 0C7.5 9.3 12 4.5 12 4.5z"],
  climate: [
    "M16.5 6.5a4 4 0 1 0-7.4 2",
    "M7.5 17h8a3.5 3.5 0 0 0 0-7 5 5 0 0 0-9.3 1.4A3 3 0 0 0 7.5 17z",
    "M9 20l-.7 1.5",
    "M14 20l-.7 1.5",
  ],
  // Same two waves the water balance canvas draws on a pond node.
  waterBalance: ["M4 10.5q4-3.5 8 0t8 0", "M4 16q4-3.5 8 0t8 0"],

  analysis: ["M3 17l5.5-5.5 3.5 3.5L20 7", "M15.5 7H20v4.5"],
  npv: [
    "M4 7.5c0-1.7 3.6-3 8-3s8 1.3 8 3-3.6 3-8 3-8-1.3-8-3z",
    "M4 7.5v9c0 1.7 3.6 3 8 3s8-1.3 8-3v-9",
    "M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3",
  ],
  ensembles: [
    "M4 8h16",
    "M4 16h16",
    "M9.5 5.8a2.2 2.2 0 1 0 0 4.4 2.2 2.2 0 0 0 0-4.4z",
    "M15 13.8a2.2 2.2 0 1 0 0 4.4 2.2 2.2 0 0 0 0-4.4z",
  ],
  closure: [
    "M12 20.5v-8",
    "M12 13c0-3.2 2.2-5.4 5.4-5.4C17.4 10.8 15.2 13 12 13z",
    "M12 16c0-2.6-1.9-4.6-4.6-4.6C7.4 14 9.4 16 12 16z",
  ],
  reconciliation: [
    "M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17z",
    "M12 7.8a4.2 4.2 0 1 0 0 8.4 4.2 4.2 0 0 0 0-8.4z",
    { d: "M12 11.1a0.9 0.9 0 1 0 0 1.8 0.9 0.9 0 0 0 0-1.8z", fill: true },
  ],

  compliance: ["M12 3l7 3v6c0 4.4-3 7.4-7 8.9-4-1.5-7-4.5-7-8.9V6l7-3z", "M9 12l2 2 4-4"],
  documents: ["M13 3H6.5v18h11V7.5L13 3z", "M13 3v4.5h4.5", "M9.5 13h5", "M9.5 16.5h5"],
  transmittals: ["M21 3.5L3 10.5l7.2 3.3L13.5 21 21 3.5z", "M10.2 13.8L21 3.5"],
  approvals: ["M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17z", "M8.5 12.2l2.4 2.4 4.6-5"],

  operations: [
    "M15.5 3.8a4.6 4.6 0 0 0 4.7 7.6l-9 9a2.4 2.4 0 0 1-3.4-3.4l9-9a4.6 4.6 0 0 0-1.3-4.2z",
  ],
  operationalData: ["M4 5h16v14H4z", "M4 9.5h16", "M4 14.5h16", "M10 9.5V19"],
  maintenance: [
    "M15.5 3.8a4.6 4.6 0 0 0 4.7 7.6l-9 9a2.4 2.4 0 0 1-3.4-3.4l9-9a4.6 4.6 0 0 0-1.3-4.2z",
    "M6 6l2.5 2.5",
  ],

  // ── Row actions ──────────────────────────────────────────────────────────
  // Same pencil ProjectDetailsPage draws on its scenario cards, so every
  // "edit this row" affordance in the app is one drawing.
  edit: ["M12 20h9", "M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z"],
  trash: [
    "M4 7h16",
    "M9.5 7V4.8A1 1 0 0 1 10.5 4h3a1 1 0 0 1 1 .8V7",
    "M6.5 7l.9 12.2A1.5 1.5 0 0 0 8.9 20.5h6.2a1.5 1.5 0 0 0 1.5-1.3L17.5 7",
    "M10.3 11v5.5",
    "M13.7 11v5.5",
  ],

  upload: ["M12 19V5.5", "M6.5 11L12 5.5l5.5 5.5", "M4.5 20.5h15"],
  plus: ["M12 5.5v13", "M5.5 12h13"],
  filter: ["M3.5 5h17l-6.5 7.5v5.5l-4 2v-7.5L3.5 5z"],
  // Opens in a new tab (InfoModal "Learn more").
  external: ["M14 4h6v6", "M20 4l-9 9", "M18 14v5.5a.5.5 0 0 1-.5.5h-13a.5.5 0 0 1-.5-.5v-13a.5.5 0 0 1 .5-.5H10"],

  // ── Notifications ────────────────────────────────────────────────────────
  bell: [
    "M18 9a6 6 0 1 0-12 0c0 5-2 6.5-2 6.5h16S18 14 18 9z",
    "M13.7 19.5a2 2 0 0 1-3.4 0",
  ],
  taskAssigned: ["M9 4h6v3H9z", "M15 5.5h3v15H6v-15h3", "M9 12l2 2 4-4"],
  taskStatus: ["M20 11a8 8 0 0 0-13.7-5.4L4 8", "M4 13a8 8 0 0 0 13.7 5.4L20 16", "M4 4v4h4", "M20 20v-4h-4"],
  comment: ["M20.5 12a7.5 7.5 0 0 1-10.9 6.7L4.5 20l1.3-5.1A7.5 7.5 0 1 1 20.5 12z"],
  mention: [
    "M12 3.5a8.5 8.5 0 1 0 4.6 15.6",
    "M12 8.2a3.8 3.8 0 1 0 0 7.6 3.8 3.8 0 0 0 0-7.6z",
    "M15.8 8.2v4.7a2.7 2.7 0 0 0 5.4 0V12a9 9 0 0 0-9-9",
  ],
  clock: ["M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17z", "M12 7.2V12l3.2 1.9"],
  alert: ["M10.6 4.1L2.9 17.4A1.6 1.6 0 0 0 4.3 20h15.4a1.6 1.6 0 0 0 1.4-2.6L13.4 4.1a1.6 1.6 0 0 0-2.8 0z", "M12 9.5v4", { d: "M12 16a0.95 0.95 0 1 0 0 1.9 0.95 0.95 0 0 0 0-1.9z", fill: true }],

  // ── Admin sidebar ────────────────────────────────────────────────────────
  companies: ["M3 21h18", "M6 21V4h8v17", "M14 10h4v11", "M9 8h2", "M9 12h2", "M9 16h2"],
  users: [
    "M9.5 4.4a3.4 3.4 0 1 0 0 6.8 3.4 3.4 0 0 0 0-6.8z",
    "M3 20c0-3.3 2.9-5.5 6.5-5.5S16 16.7 16 20",
    "M16.5 5.6a3 3 0 0 1 0 5.8",
    "M18.5 14.9c1.5.9 2.5 2.6 2.5 5.1",
  ],
  projects: ["M3 7.5A1.5 1.5 0 0 1 4.5 6h4L11 8.5h8.5A1.5 1.5 0 0 1 21 10v8a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18V7.5z"],
  billing: ["M3 6.5h18v11H3z", "M3 10.5h18", "M6.5 14.5h4"],
  support: ["M3.5 6h17v12h-17z", "M3.5 7l8.5 6 8.5-6"],
  badges: [
    "M12 3.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9z",
    "M9 12.2L7.5 21l4.5-2.6 4.5 2.6L15 12.2",
  ],
  print: [
    "M6.5 9V3.5h11V9",
    "M6.5 17H4.5a1.5 1.5 0 0 1-1.5-1.5v-5A1.5 1.5 0 0 1 4.5 9h15A1.5 1.5 0 0 1 21 10.5v5a1.5 1.5 0 0 1-1.5 1.5h-2",
    "M6.5 14h11v6.5h-11z",
  ],
};

// The admin Simulations list and the project Run Simulation action share a
// glyph deliberately — both mean "a simulation run". Aliased rather than
// duplicated so the two can never drift apart.
PATHS.simulations = PATHS.run;

export const ICON_NAMES = Object.keys(PATHS);

const Icon = ({ name, size = 18, strokeWidth = 1.8, className = "" }) => {
  const paths = PATHS[name];
  // An unknown name renders nothing rather than an error glyph — a missing
  // icon should never be louder than the label beside it.
  if (!paths) return null;

  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {paths.map((p, i) =>
        typeof p === "string"
          ? <path key={i} d={p} />
          : <path key={i} d={p.d} fill={p.fill ? "currentColor" : "none"} stroke="none" />
      )}
    </svg>
  );
};

export default Icon;
